import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { createSelector } from 'reselect';

import { sizeColumnsToFit } from '@client-shared/ui-agGrid';

import Button from '../../shared/button.component';
import AgGrid from '../../shared/agGrid/agGrid.component';
import ProgressBar from '../../shared/progressBar.component';
import CheckboxCellRenderer from '../../shared/agGrid/cellRenderers/checkboxCellRenderer.component';
import { approveTaxEntriesColumnDefs } from '../../shared/columnDefinitions/taxEntriesColumnDefs';

import {
  alignCenterCellStyle,
} from '@common/cellStyles';
import { ClientIsLockedOverlay } from '../../shared/clientLocked/ClientLockedOverlay';

export const TaxEntryProps = PropTypes.shape({
  taxEntryId: PropTypes.number.isRequired,
  companyCode: PropTypes.string.isRequired,
  taxYear: PropTypes.string.isRequired,
  countryTaxType: PropTypes.string.isRequired,
  taxEntryTypeDescription: PropTypes.string.isRequired,
  taxEntryActivityDescription: PropTypes.string.isRequired,
  bookTypeDescription: PropTypes.string.isRequired,
  adjustmentDescription: PropTypes.string,
  currencyCode: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  createdOn: PropTypes.string.isRequired,
  preparerName: PropTypes.string.isRequired,
  method: PropTypes.string,
  topsideFlag: PropTypes.string,
  fbosFlag: PropTypes.string,
});

const defaultColumnDefs = {
  ...alignCenterCellStyle,
};

class TaxEntriesForApprovalGrid extends PureComponent {
  static propTypes = {
    taxEntriesForApprovalLoading: PropTypes.bool.isRequired,
    taxEntriesForApproval: PropTypes.arrayOf(TaxEntryProps).isRequired,
    reviewTaxEntries: PropTypes.func.isRequired,
    chooseTaxEntry: PropTypes.func.isRequired,
    isTaxEntriesReviewInProgress: PropTypes.bool,
    isTaxEntriesDeclineInProgress: PropTypes.bool,
    taxEntriesReviewProgress: PropTypes.number,
  };

  state = {
    taxEntryIdsForReview: [],
    comment: '',
    missingComment: false,
    nothingSelected: false,
    sortState: null,
  };

  onCommentChange = event => {
    this.setState({
      comment: event.target.value,
    });
  };

  onGridReady = params => {
    this.gridApi = params.api;
    sizeColumnsToFit(params.api);
  };

  saveSortState = () => {
    if (this.gridApi) {
      const sortState = this.gridApi.getColumns()
        .filter(col => col.getSort())
        .map(col => ({
          colId: col.getColId(),
          sort: col.getSort(),
        }));

      this.setState({ sortState });
    }
  };

  applySortState = () => {
    const { sortState } = this.state;

    if (sortState && this.gridApi) {
      sortState.forEach(({ colId, sort }) => {
        const column = this.gridApi.getColumn(colId);
        if (column) {
          this.gridApi.applyColumnState({
            state: [{ colId, sort }],
            applyOrder: false,
          });
        }
      });
    }
  };

  onCheckboxSelectionChanged = selectedTaxEntryId => {
    this.saveSortState();
    this.setState(({ taxEntryIdsForReview }) => {
      if (taxEntryIdsForReview.includes(selectedTaxEntryId)) {
        return {
          taxEntryIdsForReview: taxEntryIdsForReview.filter(
            taxEntryId => taxEntryId !== selectedTaxEntryId
          ),
        };
      }

      return {
        taxEntryIdsForReview: [
          ...taxEntryIdsForReview,
          selectedTaxEntryId,
        ],
      };
    }, () => {
      this.refreshCells();
      this.applySortState();
    });
  };

  approveTaxEntries = () => {
    const { taxEntryIdsForReview, comment } = this.state;

    if (!taxEntryIdsForReview.length) {
      this.setState({
        nothingSelected: true,
        missingComment: false,
      });

      return;
    }

    this.props.reviewTaxEntries({
      taxEntryIds: taxEntryIdsForReview,
      reviewComment: comment,
      type: 'approve',
    });

    this.resetTaxEntryIdsForReview();
  };

  declineTaxEntries = () => {
    const { taxEntryIdsForReview, comment } = this.state;

    if (!taxEntryIdsForReview.length) {
      this.setState({
        nothingSelected: true,
        missingComment: false,
      });

      return;
    }

    if (!comment) {
      this.setState({
        missingComment: true,
        nothingSelected: false,
      });
      return;
    }

    this.props.reviewTaxEntries({
      taxEntryIds: taxEntryIdsForReview,
      reviewComment: comment,
      type: 'decline',
    });

    this.resetTaxEntryIdsForReview();
  };

  resetTaxEntryIdsForReview = () => {
    this.setState({
      taxEntryIdsForReview: [],
      comment: '',
      missingComment: false,
      nothingSelected: false,
    });
  };

  selectAll = () => {
    const filteredRows = this.getFilteredRowNodes();

    const allTaxEntryIds = filteredRows.map(row => row.data.taxEntryId);
    this.setState({
      taxEntryIdsForReview: allTaxEntryIds,
    }, () => {
      this.refreshCells();
    });
  };

  clearAll = () => {
    this.setState({
      taxEntryIdsForReview: [],
    }, () => {
      this.refreshCells();
    });
  };

  refreshCells = () => {
    this.gridApi.refreshCells({ force: true });
  };

  chooseTaxEntry = params => {
    const selectedRows = params.api.getSelectedRows();

    if (selectedRows && selectedRows.length) {
      const taxEntryId = selectedRows[0].taxEntryId;
      this.props.chooseTaxEntry(taxEntryId);
    }
  };

  selectFirstRow = params => {
    const firstRow = params.api.getModel().getRow(0);

    if (firstRow) {
      const deselectOthers = true;
      firstRow.setSelected(true, deselectOthers);
    }
  };

  getFilteredRowNodes = () => {
    const rowNodes = [];
    this.gridApi.forEachNodeAfterFilter(rowNode => rowNodes.push(rowNode));
    return rowNodes;
  };

  getColumnDefinitions = createSelector(
    taxEntryIdsForReview => taxEntryIdsForReview,
    taxEntryIdsForReview => [
      {
        headerName: '',
        field: 'taxEntryId',
        cellRenderer: CheckboxCellRenderer(),
        cellRendererParams: {
          isChecked: value => taxEntryIdsForReview.includes(value),
          onSelect: this.onCheckboxSelectionChanged,
        },
        width: 50,
        minWidth: 50,
      },
      ...approveTaxEntriesColumnDefs,
    ]);


  render() {
    const {
      taxEntriesForApprovalLoading,
      taxEntriesForApproval,
      taxEntriesReviewProgress,
      isTaxEntriesReviewInProgress,
      isTaxEntriesDeclineInProgress,
    } = this.props;

    const { comment, missingComment, nothingSelected, taxEntryIdsForReview } = this.state;
    const columnDefs = this.getColumnDefinitions(taxEntryIdsForReview);

    return (
      <div className="tax-entries-for-approval-grid page-content-container">
        {
          (isTaxEntriesReviewInProgress || isTaxEntriesDeclineInProgress ) && (
            <ProgressBar
              className="approve-tax-entries-progressbar"
              progress={taxEntriesReviewProgress}
              text="Reviewing Tax Entries"
            />
          )
        }
        <div className="row">
          <div className="col">
            <div className="d-flex flex-row justify-content-between">
              <h5>Approve Tax Entries</h5>
              {missingComment ? (
                <span className="text-danger">Comment is required for Declined Tax Entries.</span>
              ) : null}
              {nothingSelected ? (
                <span className="text-danger">No tax entry(s) are selected.</span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="ag-theme-bootstrap">
          <AgGrid
            columnDefs={columnDefs}
            context={{ componentParent: this }}
            defaultColDef={defaultColumnDefs}
            headerHeight={50}
            rowData={taxEntriesForApproval}
            onGridReady={this.onGridReady}
            onSelectionChanged={this.chooseTaxEntry}
            onRowDataUpdated={this.selectFirstRow}
            isGridLoading={taxEntriesForApprovalLoading}
          />
        </div>
        <ClientIsLockedOverlay message="You are not allowed to approve or decline tax entries while the client is locked">
          <div className="row">
            <div className="col mt-2">
              <div className="d-flex flex-row float-left">
                <Button
                  type="button"
                  kind="secondary"
                  onClick={this.selectAll}
                  disabled={taxEntriesForApprovalLoading}
                >
                  Select All
                </Button>
                <Button
                  type="button"
                  kind="secondary"
                  className="a-mx-10"
                  onClick={this.clearAll}
                  disabled={taxEntriesForApprovalLoading || taxEntryIdsForReview.length === 0}
                >
                  Clear All
                </Button>
              </div>
              <div className="d-flex flex-row float-right align-items-center">
                <label className="comment-label" htmlFor="comment">Approve/Decline Comment</label>
                <input
                  className="comment-input mr-1 px-1"
                  name="comment"
                  onChange={this.onCommentChange}
                  placeholder="Enter Comment"
                  value={comment}
                  maxLength={50}
                />
                <Button
                  type="button"
                  kind="secondary"
                  className="a-mx-10"
                  onClick={this.declineTaxEntries}
                  disabled={taxEntriesForApprovalLoading || taxEntryIdsForReview.length === 0}
                  isLoading={isTaxEntriesDeclineInProgress}
                >
                  Decline Selected
                </Button>
                <Button
                  type="button"
                  kind="primary"
                  onClick={this.approveTaxEntries}
                  disabled={taxEntriesForApprovalLoading || taxEntryIdsForReview.length === 0}
                  isLoading={isTaxEntriesReviewInProgress}
                >
                  Approve Selected
                </Button>
              </div>
            </div>
          </div>
        </ClientIsLockedOverlay>
      </div>
    );
  }
}

export default TaxEntriesForApprovalGrid;
